import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Film, FileText, Image as ImageIcon, Paperclip, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  MEDIA_COMMENT_MAX_IMAGES,
  MEDIA_IMAGE_MAX_BYTES,
  MEDIA_MESSAGE_MAX_IMAGES,
  MEDIA_POST_MAX_IMAGES,
  MEDIA_POST_MAX_VIDEOS,
  MEDIA_VIDEO_MAX_BYTES,
  POST_ATTACHMENT_MAX_COUNT,
  isImageFile,
  isVideoFile,
} from "@/lib/parseVideoEmbed";
import {
  TEAM_FILE_ACCEPT,
  TEAM_FILE_TYPES_HINT,
  isAllowedTeamFileUpload,
} from "@/lib/teamFileUpload";
import GifPickerButton from "@/components/dm/GifPickerButton";
import { toastError } from "@/lib/toast";
import { cn } from "@/lib/utils";

function capsForMode(mode) {
  if (mode === "post") {
    return { maxImages: MEDIA_POST_MAX_IMAGES, maxVideos: MEDIA_POST_MAX_VIDEOS };
  }
  if (mode === "files") {
    return {
      maxImages: POST_ATTACHMENT_MAX_COUNT,
      maxVideos: MEDIA_POST_MAX_VIDEOS,
      maxFiles: POST_ATTACHMENT_MAX_COUNT,
    };
  }
  if (mode === "comment") {
    return { maxImages: MEDIA_COMMENT_MAX_IMAGES, maxVideos: 0 };
  }
  return { maxImages: MEDIA_MESSAGE_MAX_IMAGES, maxVideos: 0 };
}

function FileThumb({ file, onRemove, disabled }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const isImage = isImageFile(file);
  const isVideo = isVideoFile(file);

  useEffect(() => {
    if (!isImage) return undefined;
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file, isImage]);

  return (
    <span className="relative inline-flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-muted/40">
      {isImage && previewUrl ? (
        <img src={previewUrl} alt={file.name} className="h-full w-full object-cover" />
      ) : isVideo ? (
        <Film className="h-5 w-5 text-muted-foreground" />
      ) : (
        <FileText className="h-5 w-5 text-muted-foreground" title={file.name} />
      )}
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        aria-label={`Remove ${file.name}`}
        className="absolute -top-1 -right-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-background border border-border shadow-sm hover:text-destructive"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

/**
 * Shared media attach UI for post/comment/message composers and card files.
 *
 * mode: 'post' | 'comment' | 'message' | 'files'
 * - post/comment/message: image (+ optional video/gif) caps
 * - files: team Files allowlist (docs, PDF, Office, images, short video)
 *
 * Imperative handle: { openPicker(), attachDisabled }
 */
const MediaAttachBar = forwardRef(function MediaAttachBar(
  {
    mode = "post",
    pendingFiles = [],
    onFilesChange,
    /** When pending images become ≥2 after a pick, parent may open the order editor */
    onRequestOrderEditor,
    /** Parent may pass hasPendingVideoEmbed to disable video upload (XOR) */
    hasPendingVideoEmbed = false,
    gif = null,
    onGifChange,
    allowGiphy = false,
    allowVideoUpload = false,
    /** When false, file input stays but the attach button is omitted (parent toolbar) */
    showAttachButton = true,
    disabled = false,
    social = false,
    orgId = null,
    /** Rendered on the same row after attach/GIF (e.g. emoji) */
    trailingActions = null,
    className,
  },
  ref
) {
  const fileInputRef = useRef(null);
  const caps = capsForMode(mode);
  const { maxImages, maxVideos } = caps;
  const maxFiles = caps.maxFiles ?? maxImages;
  const filesMode = mode === "files";

  const { imageCount, videoCount } = useMemo(() => {
    let images = 0;
    let videos = 0;
    for (const f of pendingFiles) {
      if (isVideoFile(f)) videos += 1;
      else if (isImageFile(f)) images += 1;
      else if (!filesMode) images += 1;
    }
    return { imageCount: images, videoCount: videos };
  }, [pendingFiles, filesMode]);

  const hasVideo = videoCount > 0 || hasPendingVideoEmbed;
  const atImageCap = imageCount >= maxImages;
  const atVideoCap = maxVideos <= 0 || hasVideo;
  const atFilesCap = filesMode && pendingFiles.length >= maxFiles;
  const attachDisabled = filesMode
    ? disabled || atFilesCap
    : disabled || (atImageCap && atVideoCap);
  const singleSlot = mode !== "post" && mode !== "files";
  const AttachIcon = filesMode
    ? Paperclip
    : mode === "post"
      ? ImageIcon
      : Paperclip;

  const addPickedFiles = useCallback(
    (picked) => {
      const list = Array.from(picked || []).filter(Boolean);
      if (!list.length) return;

      if (filesMode) {
        const next = [...pendingFiles];
        for (const file of list) {
          if (next.length >= maxFiles) {
            toastError(`Only ${maxFiles} files allowed`);
            break;
          }
          if (!isAllowedTeamFileUpload(file.name, file.type)) {
            toastError(
              `${file.name}: unsupported type (${TEAM_FILE_TYPES_HINT})`
            );
            continue;
          }
          const video = isVideoFile(file);
          if (video) {
            if (next.some((f) => isVideoFile(f)) || hasPendingVideoEmbed) {
              toastError("Only 1 video allowed");
              continue;
            }
            if (file.size > MEDIA_VIDEO_MAX_BYTES) {
              toastError(`${file.name}: video exceeds 100MB limit`);
              continue;
            }
          } else if (file.size > MEDIA_IMAGE_MAX_BYTES) {
            toastError(`${file.name}: exceeds 10MB limit`);
            continue;
          }
          next.push(file);
        }
        if (next.length !== pendingFiles.length) {
          onFilesChange?.(next);
          const nextImageCount = next.filter(
            (f) => isImageFile(f) && !isVideoFile(f)
          ).length;
          if (
            nextImageCount >= 2 &&
            nextImageCount > imageCount
          ) {
            onRequestOrderEditor?.();
          }
        }
        return;
      }

      const next = [...pendingFiles];
      let addedImages = 0;
      let addedVideos = 0;
      for (const file of list) {
        const video = isVideoFile(file);
        const image = !video && isImageFile(file);
        if (!image && !video) {
          toastError(`${file.name}: unsupported file type`);
          continue;
        }
        if (video) {
          if (!allowVideoUpload || maxVideos <= 0) {
            toastError("Video uploads aren't allowed here");
            continue;
          }
          if (hasVideo || addedVideos >= maxVideos) {
            toastError(`Only ${maxVideos} video allowed`);
            continue;
          }
          if (file.size > MEDIA_VIDEO_MAX_BYTES) {
            toastError(`${file.name}: video exceeds 100MB limit`);
            continue;
          }
          next.push(file);
          addedVideos += 1;
        } else {
          if (imageCount + addedImages >= maxImages) {
            toastError(
              `Only ${maxImages} image${maxImages === 1 ? "" : "s"} allowed`
            );
            continue;
          }
          if (file.size > MEDIA_IMAGE_MAX_BYTES) {
            toastError(`${file.name}: image exceeds 10MB limit`);
            continue;
          }
          next.push(file);
          addedImages += 1;
        }
      }
      if (next.length === pendingFiles.length) return;
      if (singleSlot && gif && onGifChange) onGifChange(null);
      onFilesChange?.(next);

      const nextImageCount = next.filter(
        (f) => isImageFile(f) && !isVideoFile(f)
      ).length;
      if (
        mode === "post" &&
        nextImageCount >= 2 &&
        (addedImages >= 2 || nextImageCount > imageCount)
      ) {
        onRequestOrderEditor?.();
      }
    },
    [
      filesMode,
      pendingFiles,
      maxFiles,
      hasPendingVideoEmbed,
      imageCount,
      onFilesChange,
      onRequestOrderEditor,
      allowVideoUpload,
      maxVideos,
      hasVideo,
      maxImages,
      singleSlot,
      gif,
      onGifChange,
      mode,
    ]
  );

  useImperativeHandle(
    ref,
    () => ({
      openPicker: () => {
        if (!attachDisabled) fileInputRef.current?.click();
      },
      appendFiles: addPickedFiles,
      attachDisabled,
    }),
    [addPickedFiles, attachDisabled]
  );

  const acceptTypes = filesMode
    ? TEAM_FILE_ACCEPT
    : allowVideoUpload && maxVideos > 0
      ? "image/png,image/jpeg,image/webp,image/gif,video/mp4,video/webm"
      : "image/png,image/jpeg,image/webp,image/gif";

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    addPickedFiles(picked);
  };

  const removeFile = (index) => {
    onFilesChange?.(pendingFiles.filter((_, i) => i !== index));
  };

  const handleGifSelect = (nextGif) => {
    if (singleSlot && pendingFiles.length && onFilesChange) onFilesChange([]);
    onGifChange?.(nextGif);
  };

  const showToolbarRow =
    showAttachButton ||
    allowGiphy ||
    Boolean(trailingActions) ||
    ((mode === "post" || filesMode) && imageCount >= 2);

  return (
    <div className={cn("space-y-2", className)}>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept={acceptTypes}
        multiple={mode === "post" || filesMode}
        onChange={onPickFiles}
        disabled={disabled}
      />
      {showToolbarRow ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {showAttachButton ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={() => fileInputRef.current?.click()}
              disabled={attachDisabled}
              title={
                filesMode
                  ? `Attach files (${TEAM_FILE_TYPES_HINT})`
                  : mode === "post"
                    ? `Add photos (up to ${maxImages}) or a video`
                    : "Attach an image"
              }
              aria-label={
                filesMode
                  ? "Attach files"
                  : mode === "post"
                    ? "Add photos or a video"
                    : "Attach media"
              }
            >
              <AttachIcon className="h-4 w-4" />
            </Button>
          ) : null}
          {allowGiphy ? (
            <GifPickerButton
              social={social}
              orgId={orgId}
              disabled={disabled || (singleSlot && Boolean(gif))}
              onSelect={handleGifSelect}
            />
          ) : null}
          {trailingActions}
          {(mode === "post" || filesMode) && imageCount >= 2 ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-xs text-muted-foreground"
              onClick={() => onRequestOrderEditor?.()}
              disabled={disabled}
            >
              Edit order
            </Button>
          ) : null}
        </div>
      ) : null}

      {filesMode && showAttachButton ? (
        <p className="text-xs text-muted-foreground">
          {TEAM_FILE_TYPES_HINT}. Up to {maxFiles} files (10MB each; video
          100MB).
        </p>
      ) : null}

      {pendingFiles.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {pendingFiles.map((f, i) => (
            <FileThumb
              key={`${f.name}-${i}-${f.lastModified}`}
              file={f}
              disabled={disabled}
              onRemove={() => removeFile(i)}
            />
          ))}
        </div>
      ) : null}

      {filesMode && pendingFiles.length > 0 ? (
        <ul className="space-y-0.5">
          {pendingFiles.map((f, i) => (
            <li
              key={`name-${f.name}-${i}`}
              className="truncate text-xs text-muted-foreground"
            >
              {f.name}
            </li>
          ))}
        </ul>
      ) : null}

      {gif ? (
        <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-2 py-1.5">
          <img
            src={gif.preview_url || gif.url}
            alt={gif.title || "GIF"}
            className="h-10 w-10 rounded object-cover shrink-0"
          />
          <span className="truncate flex-1 text-xs text-muted-foreground">
            {gif.title || "GIF"}
          </span>
          <button
            type="button"
            className="p-0.5 hover:text-destructive"
            onClick={() => onGifChange?.(null)}
            disabled={disabled}
            aria-label="Remove GIF"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : null}
    </div>
  );
});

export default MediaAttachBar;
